/**
 * Money and dates, in the company's own currency and locale.
 *
 * The reference implementation hard-coded the rupee sign, Indian digit
 * grouping and lakh/crore compaction into twenty-five files — correct for one
 * company in Punjab, wrong for the product. Everything here is driven by the
 * two values a workspace carries (`locale`, `currency`), and `Intl` does the
 * work, so a company in another country gets its own grouping and its own
 * compact form without a special case.
 *
 * India stays the *default* because that is where the first customers are. It
 * is a default, not a constant: nothing below reads a hard-coded locale.
 */
import type { BalanceType } from './customer.js';

export interface MoneyFormat {
    locale: string;
    currency: string;
}

export const DEFAULT_MONEY_FORMAT: MoneyFormat = { locale: 'en-IN', currency: 'INR' };

export interface MoneyFormatter {
    /** The full figure with its symbol, for editing, cheques and exports. */
    exact(value: number | null | undefined): string;
    /**
     * The dense form for tables. Receivables tables are wide and a full figure
     * costs a lot of horizontal room; the exact figure is always in the tooltip
     * and is what anything acted on uses.
     */
    compact(value: number | null | undefined): string;
    /** The figure plus which way it points, for a reminder or a balance line. */
    balance(value: number | null | undefined, type?: BalanceType): string;
    /**
     * Always to the minor unit ("₹1,23,456.78"), for a statement: every line of
     * one is checked against the customer's own books, so none may be rounded.
     */
    precise(value: number | null | undefined): string;
    /** Bare number, no symbol — for a spreadsheet cell. */
    plain(value: number | null | undefined): string;
}

const num = (value: number | null | undefined): number => {
    const n = Number(value);
    return isFinite(n) ? n : 0;
};

export function createMoneyFormatter(format: MoneyFormat = DEFAULT_MONEY_FORMAT): MoneyFormatter {
    const { locale, currency } = format;

    // Receivables run large, so minor units are visual noise — except on a
    // small residue, where dropping them would show a balance of nothing.
    const wholeUnits = new Intl.NumberFormat(locale, {
        style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
    });
    const withMinorUnits = new Intl.NumberFormat(locale, {
        style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    const compactUnits = new Intl.NumberFormat(locale, {
        style: 'currency', currency, notation: 'compact', maximumFractionDigits: 2,
    });
    const plainUnits = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });

    return {
        exact(value) {
            const abs = Math.abs(num(value));
            return (abs > 0 && abs < 1000 ? withMinorUnits : wholeUnits).format(abs);
        },
        compact(value) {
            const abs = Math.abs(num(value));
            if (abs === 0) return wholeUnits.format(0);
            return compactUnits.format(abs);
        },
        balance(value, type) {
            const amount = num(value);
            const shown = this.exact(amount);
            if (!amount) return shown;
            return `${shown} ${type === 'Cr' ? 'Cr (Excess)' : 'Dr'}`;
        },
        precise(value) {
            return withMinorUnits.format(Math.abs(num(value)));
        },
        plain(value) {
            return plainUnits.format(Math.abs(num(value)));
        },
    };
}

/* ---------------------------------- dates --------------------------------- */

export interface DateFormatter {
    long(value: Date | string | null | undefined): string;
    short(value: Date | string | null | undefined): string;
    /**
     * A business date — the calendar day, in the company's own timezone.
     *
     * A cheque is dated, not timed: it is due on the 1st wherever the person
     * reading the screen happens to be sitting.
     */
    day(value: Date | string | null | undefined): string;
    /**
     * The same day as `YYYY-MM-DD`, for seeding a date input.
     *
     * The reference implementation seeded these from the browser's UTC day and
     * a cheque recorded at 9am in Mumbai came back dated the day before. The
     * fix is not "add a few hours" — it is to ask what day it is **where the
     * company is**, which is what this does.
     */
    inputValue(value: Date | string | null | undefined): string;
    /** Today, in the company's timezone, as `YYYY-MM-DD`. */
    todayValue(): string;
    /** The time of day of a moment, in the company's timezone ("4:12 pm"). */
    time(value: Date | string | null | undefined): string;
    /**
     * A day somebody acts on, with its weekday: "Mon 26 Oct" — a next due date, a promise's day, a next follow-up.
     * A weekday a reader can check against a calendar is how a wrong year shows itself (Customer 360, 02 F10).
     */
    weekday(value: Date | string | null | undefined): string;
    /** The zone these dates are in, for a caption or a screen reader. */
    zoneLabel: string;
}

const toDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A calendar day, read as one: `'2026-12-01'` becomes local midnight on the
 * 1st, the shape `useFormatters().today` has, so the two compare as days.
 *
 * `new Date('2026-12-01')` is UTC midnight, which west of Greenwich is still
 * the evening of the 30th — and a promise read that way was "due" a day early
 * and "overdue" on the day it was due. Anything that is not exactly a real
 * `YYYY-MM-DD` is refused rather than guessed at.
 */
export function calendarDay(value: string | null | undefined): Date | null {
    const parts = DATE_ONLY.exec((value ?? '').trim());
    if (!parts) return null;
    const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day ? d : null;
}

/** A bare calendar day, as UTC midnight: formatted in UTC it is that day everywhere. */
const utcDay = (value: Date | string | null | undefined): Date | null =>
    typeof value === 'string' && calendarDay(value) ? new Date(`${value.trim()}T00:00:00Z`) : null;

export function createDateFormatter(
    locale: string = DEFAULT_MONEY_FORMAT.locale,
    timeZone?: string,
): DateFormatter {
    // An unknown zone must not blank every date on the screen, so it falls
    // back to the browser's rather than throwing at construction.
    const zone = (() => {
        if (!timeZone) return undefined;
        try {
            new Intl.DateTimeFormat('en', { timeZone }).format(new Date());
            return timeZone;
        } catch {
            return undefined;
        }
    })();

    const withZone = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions =>
        zone ? { ...options, timeZone: zone } : options;

    const long = new Intl.DateTimeFormat(locale, withZone({ day: '2-digit', month: 'short', year: 'numeric' }));
    const short = new Intl.DateTimeFormat(locale, withZone({ day: '2-digit', month: 'short' }));
    const clock = new Intl.DateTimeFormat(locale, withZone({ hour: 'numeric', minute: '2-digit' }));

    // `en-CA` renders ISO-shaped dates, which is what a date input wants, and
    // asking for the parts in the company's zone is what makes it the
    // company's day rather than the browser's.
    const iso = new Intl.DateTimeFormat('en-CA', withZone({ year: 'numeric', month: '2-digit', day: '2-digit' }));
    const asValue = (d: Date): string => iso.format(d);

    // A moment is shown in the company's zone. A bare calendar day — a
    // promise's date — is not a moment and has no zone: it is shown as the
    // day it names, which formatting it in UTC does everywhere.
    const longDay = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    const shortDay = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', timeZone: 'UTC' });
    const weekdayMoment = new Intl.DateTimeFormat(locale, withZone({ weekday: 'short', day: 'numeric', month: 'short' }));
    const weekdayDay = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    const show = (value: Date | string | null | undefined, moment: Intl.DateTimeFormat, day: Intl.DateTimeFormat) => {
        const bare = utcDay(value);
        if (bare) return day.format(bare);
        const d = toDate(value);
        return d ? moment.format(d) : '—';
    };

    return {
        long: value => show(value, long, longDay),
        short: value => show(value, short, shortDay),
        day: value => show(value, long, longDay),
        inputValue: value => {
            if (typeof value === 'string' && calendarDay(value)) return value.trim();
            const d = toDate(value);
            return d ? asValue(d) : '';
        },
        todayValue: () => asValue(new Date()),
        time: value => {
            const d = toDate(value);
            return d ? clock.format(d) : '—';
        },
        // "Mon, 26 Oct" in some locales: the comma adds nothing beside a weekday that is already a word.
        weekday: value => show(value, weekdayMoment, weekdayDay).replace(/,/g, ''),
        zoneLabel: zone ?? 'your local time',
    };
}

/**
 * A calendar day as a date input writes it: "2026-09-14", in local time.
 *
 * `toISOString().slice(0, 10)` gives the UTC day, which east of Greenwich is
 * yesterday for part of the morning — so a form opened early defaulted to a
 * date that was already overdue.
 */
export function localIsoDate(d: Date = new Date()): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** The local midnight a date input's value names; null if it is not a date. */
export function dateFromLocalIso(iso: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
}

/** Today at midnight, local time: the day every follow-up date is measured against. */
export function startOfToday(now: Date = new Date()): Date {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    return today;
}

/** Whole days from today to that date. Negative is in the past. */
export function daysFromToday(value: Date | string | null | undefined, today: Date = new Date()): number | null {
    const given = toDate(value);
    if (!given) return null;
    // A copy: the caller's date is the account's own (`bookRow` passes the last
    // contact), and moving it to midnight moved the account's recorded moment.
    const d = new Date(given);
    d.setHours(0, 0, 0, 0);
    const t = new Date(today);
    t.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - t.getTime()) / 86_400_000);
}

/** Initials for an avatar chip. */
export function initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
